import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "src/common/state/hooks";
import { Input } from "../../../common/components/Input";
import { Error } from "../../../common/components/AppState/Error";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { LoadingState, setError, setIsLoading } from "../../../common/state/slice/appStateSlice";
import { resendMfaChallenge, verifyMfa } from "../state/authSlice";
import {
  formatCountdown,
  useCountdown,
} from "../../graph/components/AssistantScenarIaRail/FailedTurnCard";
import { LOGIN_COMPONENT_ID } from "./LoginForm";

/**
 * Leg 2 of the MFA login flow (docs/plans/todo/creator-mfa-two-factor-auth.md,
 * Phase 7 step 4): a six-digit code (or, toggled, a recovery code), wired to
 * `verifyMfa`, plus - for the email method - a resend button with a
 * client-side cooldown reflecting the backend's 3-per-15-minutes limit
 * (Appendix E; the endpoint itself never reports the limit, so the cooldown
 * is a courtesy, not a mirror of server state).
 *
 * Reuses LoginForm.tsx's Input, btn-primary-fill and
 * `<Error componentId={LOGIN_COMPONENT_ID}>` pattern verbatim - this is still
 * the login screen, just its second step, so it shares the same error/loading
 * slot rather than inventing a new one.
 *
 * UX-audit hardening (D1/D2/D3/D4/D6/P1 - same doc, addendum):
 *  - D1: a 429 (limitMfaVerify, 10/15min) is a DIFFERENT failure from a wrong
 *    code and must not be reported with the same "invalid code" copy - see
 *    the rate-limit branch below, using the exact `useCountdown`/
 *    `formatCountdown` pair FailedTurnCard.tsx already built for this shape
 *    of problem (network rate-limit, live countdown, disabled retry).
 *  - D2: the server burns the challenge on the 5th failed attempt but still
 *    returns the same generic error on that 5th response - the ONLY way the
 *    client can tell "this was the last one" is to count client-side and go
 *    terminal before ever attempting a 6th. Decided with the user: count
 *    locally, do NOT ask the backend to distinguish attempt N from attempt 1
 *    (that would be a state oracle for an attacker), and do NOT auto-redirect
 *    - keep the user on this screen with an explicit "Back to sign in".
 *  - D3: the resend endpoint always resolves {ok:true} (anti-enumeration) but
 *    the real limit is 3/15min - cap the affordance client-side so the UI
 *    stops promising a send past that point.
 *  - D4: leaving isSubmitting untouched on success (see the .then() below) is
 *    intentional - re-enabling the button right as the page navigates away is
 *    a flicker, not a confirmation.
 *  - D6: authenticator apps often render "123 456" - stripping non-digits on
 *    paste (before the native maxLength truncates the pasted string) and on
 *    change is new logic with no visual precedent, approved as part of this
 *    pass.
 */

const CODE_PATTERN = /^\d{6}$/;
const RESEND_COOLDOWN_SECONDS = 30;
const RESEND_WINDOW_MS = 15 * 60 * 1000;
const MAX_RESENDS_PER_WINDOW = 3;
const MAX_VERIFY_ATTEMPTS = 5;
// The real window (mfaRateLimit.js's limitMfaVerify, FIFTEEN_MINUTES_MS) is
// never reported to the client on a 429 - same "courtesy, not a mirror of
// server state" philosophy as RESEND_COOLDOWN_SECONDS above. If the user
// retries after this and is still limited, the real request 429s again and
// restarts the countdown - it never lies by re-enabling the button early.
const RATE_LIMIT_COOLDOWN_SECONDS = 60;

interface MfaChallengeFormProps {
  mfaToken: string;
  method: "totp" | "email" | null;
  /** Email method only: the mailbox the code went to, shown in full so the
   * user knows where to look. Null for TOTP, where no mail is involved. */
  sentTo?: string | null;
  /** Dispatched on the explicit "back to sign in" link AND on a
   * "mfa_challenge_invalid" response (Phase 7 step 6) - the 5-minute token
   * expired or was already consumed, and a dead-end screen with no way back
   * is the predictable failure to avoid there. */
  onBackToCredentials: () => void;
}

const MfaChallengeForm = ({
  mfaToken,
  method,
  sentTo = null,
  onBackToCredentials,
}: MfaChallengeFormProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [code, setCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSent, setResendSent] = useState(false);

  // D3: client-side cap on the resend affordance, same courtesy-cooldown
  // mechanism as resendCooldown above (no new mechanism, no server round-trip
  // to "check" the limit) - just also counting how many sends happened inside
  // the current 15-minute window.
  const [resendCount, setResendCount] = useState(0);
  const [resendWindowStart, setResendWindowStart] = useState<number | null>(null);

  // D2: attempts are counted client-side ONLY. The backend deliberately
  // returns one generic error for wrong/expired/replayed/burned codes alike -
  // asking it to also say "this was attempt #5" would be a state oracle for
  // an attacker. The browser already knows how many times IT submitted.
  const [failedAttempts, setFailedAttempts] = useState(0);

  // D1: set the moment a 429 comes back; cleared once the countdown reaches
  // zero (see the effect below).
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);

  const isTerminal = failedAttempts >= MAX_VERIFY_ATTEMPTS;
  const rateLimitRemainingSeconds = useCountdown(rateLimitedUntil ?? undefined);
  const isRateLimited = rateLimitedUntil !== null && rateLimitRemainingSeconds > 0;

  // D4: LOGIN_COMPONENT_ID is shared with LoginForm.tsx's credentials step
  // (see the file header) - leg 1's signIn.fulfilled leaves it in
  // LoadingState.Success on a plain "mfa_required"/"mfa_enrollment_required"
  // response (it resolved normally, it just wasn't a full login yet). Now
  // that showSuccess is on below, that stale Success would otherwise flash
  // the checkmark the instant this screen mounts, before the user has typed
  // anything. Reset it once on mount.
  useEffect(() => {
    dispatch(setIsLoading({ componentId: LOGIN_COMPONENT_ID, loading: LoadingState.Idle }));
  }, [dispatch]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }
    const timer = setInterval(() => setResendCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // The 3-per-15-minute resend budget self-clears once its window elapses,
  // even without another user action - otherwise the button would stay
  // disabled forever past the real window instead of quietly becoming
  // available again.
  useEffect(() => {
    if (resendWindowStart === null) {
      return undefined;
    }
    const remaining = RESEND_WINDOW_MS - (Date.now() - resendWindowStart);
    if (remaining <= 0) {
      setResendCount(0);
      setResendWindowStart(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      setResendCount(0);
      setResendWindowStart(null);
    }, remaining);
    return () => clearTimeout(timer);
  }, [resendWindowStart]);

  // Once the rate-limit countdown reaches zero, drop back to the normal error
  // slot - and clear whatever stale backend message is still sitting in Redux
  // (Priority 1 of loaderHandleMiddleware) so it doesn't flash the generic
  // "invalid code" copy the instant the countdown ends, before the user has
  // even retried anything.
  useEffect(() => {
    if (rateLimitedUntil !== null && rateLimitRemainingSeconds <= 0) {
      setRateLimitedUntil(null);
      dispatch(setError({ componentId: LOGIN_COMPONENT_ID, error: "" }));
    }
  }, [rateLimitRemainingSeconds, rateLimitedUntil, dispatch]);

  const resendLimitReached = resendCount >= MAX_RESENDS_PER_WINDOW;

  const handleToggleRecoveryCode = () => {
    setUseRecoveryCode(!useRecoveryCode);
    setCode("");
  };

  const focusCodeField = () => {
    // Input/CommonInput don't forward a ref (plain function components), so
    // the field is reached the same way its own data-testid is used in
    // tests - by query, not by an imperative ref plumbed through props.
    // setTimeout(0) so this runs after React has committed the DOM update.
    setTimeout(() => {
      document.querySelector<HTMLInputElement>("[data-testid='mfa-challenge-code']")?.focus();
    }, 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsSubmitting(true);
    dispatch(
      verifyMfa({
        componentId: LOGIN_COMPONENT_ID,
        mfaToken,
        ...(useRecoveryCode ? { recoveryCode: code.trim() } : { otp: code.trim() }),
      }),
    )
      .unwrap()
      .then(() => {
        // D4: intentionally NOT setIsSubmitting(false) here. Until now the
        // only signal a correct code succeeded was a silent route change
        // (LoginFormContainer's [jwt] effect) - the field kept the submitted
        // value, no spinner, no confirmation. Leaving the button
        // disabled/spinner-showing all the way through navigation (instead of
        // re-enabling it right as the page changes) plus IsLoading's
        // showSuccess below gives this highest-stakes moment a visible
        // confirmation instead of nothing. The component unmounts on
        // navigation before isSubmitting would ever need resetting.
      })
      .catch(
        (
          err: { error?: { message?: string }; status?: number } | undefined,
        ) => {
          setIsSubmitting(false);
          // Clear the field on failure. Without this the rejected code stays
          // put, and because maxLength is already reached a paste of the next
          // code is silently DROPPED by the browser - the user sees the old
          // value sitting there and has to clear it by hand every time. Worse,
          // a partial edit can submit a mangled value and burn one of only five
          // attempts. Reported from real use, not theory.
          setCode("");
          // P1: a user who clicked "Verify" must be able to just start typing
          // the next code, not click back into the box first.
          focusCodeField();

          // D1: a 429 means the request was rejected by limitMfaVerify BEFORE
          // the code was ever checked (mfaVerify.js returns early) - it is not
          // a wrong code and must not count against the 5-attempt budget
          // below, nor be reported with "invalid code" copy.
          if (err?.status === 429) {
            setRateLimitedUntil(Date.now() + RATE_LIMIT_COOLDOWN_SECONDS * 1000);
            return;
          }

          // Both remaining failures are HTTP 400 with a deliberately generic
          // message (Appendix A) - only "mfa_challenge_invalid" (expired/
          // consumed token) sends the user back; "mfa_invalid_code" (wrong
          // code) leaves them here to retry, with the error already surfaced
          // by <Error componentId={LOGIN_COMPONENT_ID}>.
          if (err?.error?.message === "mfa_challenge_invalid") {
            onBackToCredentials();
            return;
          }

          // D2: count it. The 5th rejection here looks IDENTICAL to the 1st
          // through 4th to the server (still "mfa_invalid_code") - the burn
          // only becomes visible on a 6th attempt this UI must never make.
          setFailedAttempts((n) => n + 1);
        },
      );
  };

  const handleResend = () => {
    if (resendCooldown > 0 || resendLimitReached) {
      return;
    }
    setResendSent(false);
    dispatch(resendMfaChallenge({ componentId: LOGIN_COMPONENT_ID, mfaToken }))
      .unwrap()
      .then(() => {
        setResendSent(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch(() => {
        // The endpoint always resolves {ok: true} server-side (Appendix E) -
        // a rejection here is a network/HTTP problem, not "no code was sent".
        // Still start the cooldown: hammering retry into a failing request
        // isn't the fix, and the cooldown is a courtesy either way.
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .finally(() => {
        setResendCount((c) => c + 1);
        setResendWindowStart((start) => start ?? Date.now());
      });
  };

  // D6: strip non-digits before maxLength applies. A native <input
  // maxLength=6> truncates a pasted "123 456" to "123 45" (space included)
  // BEFORE onChange ever sees it, so onPaste must intercept the clipboard
  // directly rather than clean up after the fact - by the time onChange
  // fires, the trailing "6" is already gone. Recovery codes are alphanumeric
  // with dashes and are deliberately left untouched.
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(useRecoveryCode ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6));
  };

  const handleCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (useRecoveryCode) {
      return;
    }
    e.preventDefault();
    setCode(e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="my-2">
        <h2 className="text-xl font-medium text-black">{t("pages.login.mfaChallenge.title")}</h2>
        <p className="text-sm">
          {isTerminal
            ? t("pages.login.mfaChallenge.tooManyAttemptsSubtitle")
            : useRecoveryCode
              ? t("pages.login.mfaChallenge.subtitleRecoveryCode")
              : method === "email"
                ? sentTo
                  ? t("pages.login.mfaChallenge.subtitleEmailTo", { email: sentTo })
                  : t("pages.login.mfaChallenge.subtitleEmail")
                : t("pages.login.mfaChallenge.subtitleTotp")}
        </p>
      </div>

      {isTerminal ? (
        <p className="text-sm text-red-600 font-medium my-2" role="alert">
          {t("pages.login.mfaChallenge.tooManyAttempts")}
        </p>
      ) : (
        <>
          {/* NOT changePassword.error ("The password is wrong!"). LoginForm uses
              that string legitimately — there, a wrong password IS the cause — but
              on this screen the user typed a CODE, and telling them their password
              is wrong sends them back to re-enter credentials that were correct.
              Observed in a browser before it was fixed. The wording stays generic
              on purpose: the backend deliberately returns one error for wrong,
              expired, replayed and burned codes alike, and the UI must not invent
              a distinction it isn't given. Hidden while rate-limited (D1) - that
              slot shows a DIFFERENT, more accurate message below instead. */}
          {!isRateLimited && (
            <Error
              componentId={LOGIN_COMPONENT_ID}
              errorString={t("pages.login.mfaChallenge.invalidCode")}
            />
          )}
          {/* D1: reserved space (invisible when not applicable) so nothing
              shifts when a 429 appears - precedent: RateLimitLine.tsx. */}
          <p
            className={`text-xs text-amber-600 my-1 ${isRateLimited ? "" : "invisible"}`}
            role="status"
          >
            {t("pages.login.mfaChallenge.rateLimited", {
              time: formatCountdown(rateLimitRemainingSeconds),
            })}
          </p>
          <IsLoading componentId={LOGIN_COMPONENT_ID} showSuccess />

          <Input
            autoFocus
            type="text"
            // "one-time-code" tells the browser this is an OTP: it offers the
            // SMS/authenticator suggestion instead of a generic autofill history
            // of previously typed values. Recovery codes are not one-time-codes in
            // that sense and must never be remembered, hence "off".
            autoComplete={useRecoveryCode ? "off" : "one-time-code"}
            inputMode={useRecoveryCode ? "text" : "numeric"}
            maxLength={useRecoveryCode ? 19 : 6}
            readOnly={false}
            value={code}
            onChange={handleCodeChange}
            onPaste={handleCodePaste}
            className="font-medium bg-white text-gray-600 border-0 tracking-[0.3em] text-center"
            placeholder={
              useRecoveryCode
                ? t("pages.login.mfaChallenge.recoveryCodePlaceholder")
                : t("pages.login.mfaChallenge.codePlaceholder")
            }
            data-testid="mfa-challenge-code"
          />

          <div className="flex justify-between items-center mt-2">
            <button
              type="button"
              className="text-purple-500 font-medium text-xs border-0"
              onClick={handleToggleRecoveryCode}
            >
              {useRecoveryCode
                ? t("pages.login.mfaChallenge.useCodeInstead")
                : t("pages.login.mfaChallenge.useRecoveryCodeInstead")}
            </button>

            {/* Space reserved even when hidden (TOTP method / recovery-code mode)
                so toggling never shifts the layout. */}
            <button
              type="button"
              className={`text-purple-500 font-medium text-xs border-0 disabled:text-gray-400 ${
                method === "email" && !useRecoveryCode ? "" : "invisible"
              }`}
              onClick={handleResend}
              disabled={resendCooldown > 0 || resendLimitReached}
            >
              {resendLimitReached
                ? t("pages.login.mfaChallenge.resendLimitReached")
                : resendCooldown > 0
                  ? t("pages.login.mfaChallenge.resendCooldown", { seconds: resendCooldown })
                  : t("pages.login.mfaChallenge.resend")}
            </button>
          </div>

          <p
            className={`text-xs text-green-600 mt-1 ${
              resendSent && method === "email" ? "" : "invisible"
            }`}
          >
            {t("pages.login.mfaChallenge.resendSent")}
          </p>
        </>
      )}

      <div className="flex justify-between items-center pt-3">
        <button
          type="button"
          className="text-purple-500 font-medium text-xs border-0"
          onClick={onBackToCredentials}
        >
          {t("pages.login.mfaChallenge.backToLogin")}
        </button>
        {!isTerminal && (
          <button
            className="btn-primary-fill"
            type="submit"
            disabled={
              isSubmitting ||
              isRateLimited ||
              (useRecoveryCode ? code.trim().length === 0 : !CODE_PATTERN.test(code.trim()))
            }
          >
            {t("pages.login.mfaChallenge.submit")}
          </button>
        )}
      </div>
    </form>
  );
};

export default MfaChallengeForm;
